import { useCallback, useEffect, useState } from 'react';
import { CurrencyJpy, CaretDown, CaretRight } from '@phosphor-icons/react';
import { devApi } from './devApi';

const yen = (n) => (n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`);

const Stat = ({ label, value, hint, tone = 'text-zinc-900', testid }) => (
  <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid={testid}>
    <p className="text-xs font-semibold text-zinc-600">{label}</p>
    <p className={`text-lg font-bold tabular-nums ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-zinc-500">{hint}</p>}
  </div>
);

/** 原価の積み上げ — 材料費（計画）/ 試作費（実績）/ スクラップ費 / 最終原価。 */
export const CostCard = ({ devId }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    devApi.costs(devId).then(setD).catch(() => {});
  }, [devId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-costs">
      <button type="button" onClick={() => setOpen(!open)} data-testid="dev-costs-toggle"
        className="flex w-full items-center gap-2 border-b border-zinc-200 bg-zinc-100 px-4 py-2 text-left hover:bg-zinc-200/60">
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
          <CurrencyJpy size={15} weight="duotone" className="text-teal-700" /> 原価の積み上げ
        </p>
        <p className="text-xs text-zinc-600">
          材料 {yen(d.material_cost)} / 試作 {yen(d.prototype_cost)} / 最終 {yen(d.final_cost)}
        </p>
      </button>
      <div className="space-y-3 p-4">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          <Stat label="材料費（計画）" value={yen(d.material_cost)}
            hint={d.build_quantity ? `${d.build_quantity}台分` : '製作台数未入力'}
            testid="dev-cost-material" />
          <Stat label="試作費（実際に投入）" value={yen(d.prototype_cost)}
            testid="dev-cost-prototype" />
          {/* ¥0 と「単価が分からない」を同じ顔で出さない。読む人の目の前で件数を言う。 */}
          <Stat
            label="スクラップ費"
            value={d.scrap_rows > 0 && !d.scrap_cost_known && d.scrap_cost === 0
              ? '不明' : yen(d.scrap_cost)}
            tone="text-rose-700"
            hint={d.scrap_rows === 0 ? 'スクラップなし'
              : d.scrap_unknown_rows > 0
                ? `単価未登録 ${d.scrap_unknown_rows}行（${d.scrap_unpriced_qty}）は未計上`
                : null}
            testid="dev-cost-scrap" />
          <Stat label="使用材料" value={yen(d.material_usage_cost)}
            hint={d.material_usage_planned
              ? `計画 ${yen(d.material_usage_planned)}（自社製作部品の材料）` : '自社製作の材料なし'}
            testid="dev-cost-material-usage" />
          <Stat label="在庫へ返却（差引）" value={yen(d.returned_credit)} tone="text-emerald-700"
            testid="dev-cost-returned" />
          <Stat label="最終原価" value={yen(d.final_cost)} tone="text-teal-800"
            hint={d.per_unit_cost != null ? `1台あたり ${yen(d.per_unit_cost)}` : null}
            testid="dev-cost-final" />
        </div>
        {d.note && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900"
            data-testid="dev-cost-note">{d.note}</p>
        )}
        {open && (
          <div className="overflow-x-auto" data-testid="dev-cost-detail">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-left text-xs text-zinc-600">
                <tr>
                  <th className="px-2 py-1.5">明細</th>
                  <th className="px-2 py-1.5 text-right">単価</th>
                  <th className="px-2 py-1.5 text-right">数量</th>
                  <th className="px-2 py-1.5 text-right">金額</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {d.material_lines.map((l) => (
                  <tr key={`m-${l.part_id}`}>
                    <td className="px-2 py-1.5">計画: {l.name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {l.unit_cost_known ? yen(l.unit_cost)
                        : <span className="text-amber-700">単価未登録</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}{l.uom}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{yen(l.amount)}</td>
                  </tr>
                ))}
                {d.prototype_lines.map((l) => (
                  <tr key={`p-${l.id}`}>
                    <td className="px-2 py-1.5">
                      投入: {l.name}
                      {l.scrapped_qty ? `（スクラップ ${l.scrapped_qty}）` : ''}
                      {l.returned_qty ? `（返却 ${l.returned_qty}）` : ''}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {l.unit_cost_known ? yen(l.unit_cost)
                        : <span className="text-amber-700">単価未登録</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}{l.uom}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {l.unit_cost_known ? yen(l.amount) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
